import { Game } from '../game/game';
import { del, get, post } from '../util/fetch';
import { ResourceType } from './board/board-view';
import { CatanView, DevelopmentCardKind } from './catan-view';

/**
 * A debug request body: replace the game's state with the named phase, plus
 * whatever attributes that phase needs.
 */
export interface DebugState {
  state: string;
  turnOrder?: string[];
  seed?: number;
  /** How far into the turn order a replaced play phase starts. */
  turnIndex?: number;
  /**
   * Pieces to put on the board of a replaced play phase, so a situation can
   * start from a position rather than from an empty board.
   */
  pieces?: {
    piece: 'Settlement' | 'City' | 'Road';
    locationId: number;
    playerId: string;
  }[];
  /** The award holders a replaced play phase starts with. */
  longestRoadPlayerId?: string;
  largestArmyPlayerId?: string;
  /** Victory point cards to deal, so a total can start near the win. */
  victoryPointCards?: { playerId: string; count: number }[];
}

/**
 * The development-only console hook: every debug operation a tester needs,
 * callable from JavaScript alone with no UI. It reuses the same host endpoints
 * the client uses — `/game/{id}/state` (refused unless the host runs with
 * DebugEnabled, locked and validated like a normal action) and the existing
 * `/auth/impersonate` route for acting as another player — and logs what came
 * back so the console reports the result.
 */
export interface CatanDebug {
  /** The user id this browser profile is currently acting as. */
  me(): Promise<string>;
  /** Act as another player (development only; the host refuses otherwise). */
  impersonate(userId: string): Promise<string>;
  /** Go back to acting as this browser profile's own user. */
  stopImpersonating(): Promise<string>;
  /** Resolve a join code to the game, so its id can be used by the calls below. */
  lookup(code: string): Promise<Game>;
  /** The current view of a game. */
  view(gameId: string): Promise<CatanView>;
  /** Replace the whole game state by hand, jumping it into any phase. */
  setState(gameId: string, state: DebugState): Promise<CatanView>;
  /**
   * Start the game without a full table, taking the turn order as a given
   * rather than drawing it, and pinning the seed the later phases draw from.
   * This is the scripted-action pattern: a debug-only variant of a normal
   * action whose outcome is supplied instead of drawn.
   */
  startGame(
    gameId: string,
    turnOrder: string[],
    seed?: number,
  ): Promise<CatanView>;
  /**
   * Roll with the dice given rather than drawn from the seed, so a turn can be
   * exercised against a known number. The dice are the two a normal roll would
   * have drawn.
   */
  rollDice(gameId: string, dice: number[]): Promise<CatanView>;
  /**
   * Hand a player resources directly, so a turn can be exercised without
   * waiting for production. The player is a player-row id, the one a view lists.
   */
  grantResources(
    gameId: string,
    playerId: string,
    resources: Partial<Record<ResourceType, number>>,
  ): Promise<CatanView>;
  /**
   * Buy a development card with the card named rather than drawn, so a tester
   * can reach any of the five kinds — the hidden victory point included. The
   * price and the one-card-a-turn limit are the ordinary buy's.
   */
  buyDevelopmentCard(
    gameId: string,
    card: DevelopmentCardKind,
  ): Promise<CatanView>;
}

const report = <T>(label: string, value: T): T => {
  // eslint-disable-next-line no-console
  console.log(`[catan debug] ${label}`, value);
  return value;
};

const debug: CatanDebug = {
  me: () => get<string>('/auth').then((id) => report('you are acting as', id)),
  impersonate: (userId: string) =>
    post<void>('/auth/impersonate', userId).then(() =>
      report('now acting as', userId),
    ),
  stopImpersonating: () =>
    del<void>('/auth/impersonate').then(() =>
      report('now acting as yourself', 'ok'),
    ),
  lookup: (code: string) =>
    get<Game>(`/game/lookup/${code}`).then((game) =>
      report(`game ${code.toUpperCase()}`, game),
    ),
  view: (gameId: string) =>
    get<CatanView>(`/game/${gameId}/state`).then((view) =>
      report(`game ${gameId}`, view),
    ),
  setState: (gameId: string, state: DebugState) =>
    post<CatanView>(`/game/${gameId}/state`, state).then((view) =>
      report(`game ${gameId} after state ${state.state}`, view),
    ),
  startGame: (gameId: string, turnOrder: string[], seed?: number) =>
    post<CatanView>(`/game/${gameId}/state`, {
      action: 'StartGame',
      turnOrder,
      seed,
    }).then((view) => report(`game ${gameId} started`, view)),
  rollDice: (gameId: string, dice: number[]) =>
    post<CatanView>(`/game/${gameId}/state`, {
      action: 'RollDice',
      dice,
    }).then((view) => report(`game ${gameId} rolled ${dice.join(' + ')}`, view)),
  grantResources: (
    gameId: string,
    playerId: string,
    resources: Partial<Record<ResourceType, number>>,
  ) =>
    post<CatanView>(`/game/${gameId}/state`, {
      action: 'GrantResources',
      player: playerId,
      resources,
    }).then((view) => report(`game ${gameId} granted resources`, view)),
  buyDevelopmentCard: (gameId: string, card: DevelopmentCardKind) =>
    post<CatanView>(`/game/${gameId}/state`, {
      action: 'BuyDevelopmentCard',
      card,
    }).then((view) => report(`game ${gameId} bought a ${card}`, view)),
};

/**
 * Puts the hook on `window.catanDebug`. It is deliberately absent from
 * production builds: the debug endpoints it drives are themselves refused by
 * the host outside development, and the hook has nothing to do there.
 */
export const installCatanDebug = (): void => {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  (window as Window & { catanDebug?: CatanDebug }).catanDebug = debug;
};
